<template>
  <div
    id="new-intruction-panel"
    class="fixed w-screen top-18 flex justify-end transition-all duration-500"
    :style="{
      right: open ? '0vw' : '-100vw',
      height: 'calc(100vh - 4.5rem)',
      zIndex: 51
    }"
  >
    <div
      class="h-full w-screen absolute left-0 top-0 -z-10"
      @click="$emit('close-panel')"
    ></div>
    <div
      class="h-full w-5 rounded-l-3xl flex-shrink-0 relative z-0"
      :style="{
        background: 'linear-gradient(180deg, #FFE4E8 0%, #9A8AC2 100%)'
      }"
    ></div>

    <div
      class="h-full w-120 flex-shrink-0 flex flex-col bg-pure-white relative z-0"
    >
      <div class="py-4 px-6 flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <div
            class="relative rounded-full flex-shrink-0 min-h-6 px-3 flex items-center"
            :class="[
              stage.is_discontinued
                ? 'bg-rose-200'
                : stage.is_admitted
                ? 'bg-lime-200'
                : 'bg-cornflower-200'
            ]"
          >
            <BlTextSubtitle3
              :text="stage.name"
              :class="[
                stage.is_discontinued
                  ? 'text-rose-700'
                  : stage.is_admitted
                  ? 'text-lime-700'
                  : 'text-cornflower-800'
              ]"
            />
          </div>
          <BlIcon
            v-if="open"
            icon="close"
            color="slate-6"
            class="h-6 w-6"
            clickable
            @clicked="$emit('close-panel')"
          />
        </div>
        <BlTextTitle4 text="Stage instructions" />
      </div>
      <div class="px-6 py-4 rounded-t-3xl bg-light-purple-300">
        <BlTextSubtitle2
          text="Create new instruction"
          class="text-pure-white"
        />
      </div>
      <div
        class="relative h-full flex flex-col justify-between overflow-y-auto"
      >
        <div class="py-5 px-6 flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <div
              v-for="opt in typeOptions"
              :key="opt.value"
              class="h-7.5 px-2 flex items-center gap-2 rounded-full bg-lime-100 cursor-pointer border"
              :class="[
                opt.value === typeInput ? 'border-lime-600' : 'border-lime-100'
              ]"
              @click="typeInput = opt.value"
            >
              <BlIcon
                v-if="opt.value === 'notes'"
                icon="paper-note"
                color="lime-6"
                class="h-4 w-4"
              />
              <BlIcon
                v-if="opt.value === 'todo'"
                icon="to-do"
                color="lime-6"
                class="h-4 w-4"
              />
              <BlTextBody2 :text="opt.label" class="text-lime-700" />
            </div>
          </div>
          <div
            v-if="typeInput === 'todo'"
            class="p-2 flex gap-2 bg-cornflower-200 rounded"
          >
            <BlIcon icon="lamp" color="cornflower-8" class="h-4 w-4" />
            <BlTextBody2
              text="When you set a to-do, you can assign people to complete the task when the prospect moves to this stage."
              class="text-cornflower-800"
            />
          </div>
          <BlInputText :input="titleInput" @changed="inputHandler" />
          <div v-if="open" class="pb-px relative">
            <VueEditor
              :editor-toolbar="customToolbar"
              v-model="descriptionInput"
              class="blum-editor"
              placeholder="Description"
            />
          </div>
          <div
            v-if="uploads.length"
            class="p-2 flex flex-col gap-2 rounded bg-slate-200"
          >
            <BlFileHolder
              v-for="upload in uploads"
              :key="upload.lastModified"
              :name="upload.name"
              :size="upload.size"
              removeable
              @remove-handler="onRemoveDoc(upload)"
            />
          </div>
          <div class="flex items-center justify-between">
            <input
              type="file"
              :name="`new_upload_document_stage_${stage.id}`"
              :id="`new_upload_document_stage_${stage.id}`"
              @change="onAddDoc"
              hidden
            />
            <BlInputButton
              v-if="open"
              type="plain"
              dense
              icon="plus"
              label="Add document"
              @clicked="openUpload"
            />
            <BlTextBody2
              v-if="uploads.length"
              :text="`${uploads.length} file(s) added`"
            />
          </div>
        </div>
        <div
          class="h-18 px-6 pt-4 sticky bottom-0 flex-shrink-0 bg-pure-white grid"
        >
          <BlInputButton
            label="Create"
            :disabled="isDisabledSubmit"
            :loading="submitLoading"
            @clicked="onCreate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { VueEditor } from "vue2-editor";

import BlIcon from "../../BlIcon.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputText from "../../BlInputText.vue";
import BlFileHolder from "../../BlFileHolder.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "NewInstructionStagePanel",
  components: {
    VueEditor,
    BlIcon,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputText,
    BlFileHolder,
    BlInputButton
  },
  props: {
    open: { type: Boolean, default: false },
    stage: { type: Object, required: true }
  },
  data() {
    return {
      submitLoading: false,
      typeInput: "notes", // notes | todo
      typeOptions: [
        { value: "notes", label: "Notes" },
        { value: "todo", label: "To-do" }
      ],
      titleInput: {
        placeholder: "Title",
        model: ""
      },
      descriptionInput: "",
      customToolbar: [["bold", "italic", "underline", "link"]],
      uploads: [],
      files: []
    };
  },
  computed: {
    isDisabledSubmit() {
      if (!this.titleInput.model) return true;
      return false;
    }
  },
  watch: {
    open() {
      this.typeInput = "notes";
      this.titleInput.model = "";
      this.descriptionInput = "";
      this.uploads = [];
      this.files = [];
    }
  },
  methods: {
    ...mapActions(["createInstruction"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    openUpload() {
      const input = document.getElementById(
        `new_upload_document_stage_${this.stage.id}`
      );
      if (input) input.click();
    },
    byteConverter(bytes, decimals, only) {
      const K_UNIT = 1024;
      const SIZES = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

      if (bytes === 0) return "0 Byte";

      if (only === "MB") {
        return (bytes / (K_UNIT * K_UNIT)).toFixed(decimals) + " MB";
      }

      let i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
      let resp =
        parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) +
        " " +
        SIZES[i];

      return resp;
    },
    onAddDoc(e) {
      const files = e.target.files;
      if (files && files.length) {
        const upload = {
          lastModified: files[0].lastModified,
          name: files[0].name,
          size: files[0].size,
          sizeString: this.byteConverter(files[0].size, 2)
        };
        this.files = [...this.files, files[0]];
        this.uploads = [...this.uploads, upload];
      }
    },
    onRemoveDoc(upload) {
      const index = this.uploads.findIndex(
        i => i.lastModified === upload.lastModified
      );
      if (index > -1) {
        this.files = this.files.filter(
          i => i.lastModified !== upload.lastModified
        );
        this.uploads = this.uploads.filter(
          i => i.lastModified !== upload.lastModified
        );
      }
    },
    onCreate() {
      const formData = new FormData();
      formData.append("instruction[instructionable_id]", this.stage.id);
      formData.append("instruction[instructionable_type]", "Stage");
      formData.append("instruction[instruction_type]", this.typeInput);
      formData.append("instruction[title]", this.titleInput.model);
      formData.append("instruction[description]", this.descriptionInput);
      this.files.forEach((file, idx) => {
        formData.append(`instruction[documents][${idx}]`, file);
      });
      const jsonData = {
        instruction: {
          instructionable_id: this.stage.id,
          instructionable_type: "Stage",
          instruction_type: this.typeInput,
          title: this.titleInput.model,
          description: this.descriptionInput,
          documents: []
        }
      };
      const data = this.files.length ? formData : jsonData;
      this.submitLoading = true;
      this.createInstruction(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.$emit("close-panel");
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/modal/NewInstructionStagePanel.vue